import type { PettyCashVoucher, TimesheetEntry, BillingInvoice } from './ledgersync.schema'
import { AlphalistCompiler } from './alphalist.compiler'

export interface PettyCashFundStatus {
  totalFund: number // ₱50,000 default petty cash float
  currentBalance: number
  isBelowThreshold: boolean // true if balance < ₱5,000
  thresholdAmount: number // ₱5,000
  needsReplenishment: boolean
}

export class LedgerSyncService {
  /**
   * Calculates petty cash fund balance and checks if < ₱5,000 threshold alert should trigger.
   */
  static evaluatePettyCashFund(
    vouchers: PettyCashVoucher[],
    initialFloat: number = 50000,
    threshold: number = 5000
  ): PettyCashFundStatus {
    const disbursedSum = vouchers
      .filter((v) => v.status === 'disbursed' || v.status === 'approved')
      .reduce((sum, v) => sum + v.amount, 0)

    const currentBalance = initialFloat - disbursedSum
    const isBelowThreshold = currentBalance < threshold

    return {
      totalFund: initialFloat,
      currentBalance,
      isBelowThreshold,
      thresholdAmount: threshold,
      needsReplenishment: isBelowThreshold,
    }
  }

  /**
   * Converts approved timesheets into billing invoice items.
   */
  static convertTimesheetsToInvoice(
    clientName: string,
    timesheets: TimesheetEntry[]
  ): { totalBillable: number; items: Array<{ description: string; amount: number }> } {
    const items = timesheets.map((ts) => ({
      description: `${ts.taskDescription} (${ts.hoursSpent} hrs @ ₱${ts.hourlyRate}/hr by ${ts.accountantName})`,
      amount: ts.billableAmount,
    }))

    const totalBillable = items.reduce((sum, i) => sum + i.amount, 0)
    return { totalBillable, items }
  }

  /**
   * Aggregates weekly collections for Friday 5 PM report.
   */
  static aggregateWeeklyCollections(
    invoices: BillingInvoice[],
    weekLabel: string
  ): { week: string; totalCollected: number; paidInvoiceCount: number; collectionRatePercent: number } {
    const weekInvoices = invoices.filter((i) => i.weeklyCollectionWeek === weekLabel)
    const paidInvoices = weekInvoices.filter((i) => i.status === 'paid')

    const totalCollected = paidInvoices.reduce((sum, i) => sum + i.totalAmount, 0)
    const totalBilled = weekInvoices.reduce((sum, i) => sum + i.totalAmount, 0)
    const collectionRatePercent = totalBilled > 0 ? (totalCollected / totalBilled) * 100 : 100

    return {
      week: weekLabel,
      totalCollected,
      paidInvoiceCount: paidInvoices.length,
      collectionRatePercent,
    }
  }
}
